import messageDryUrl from "@/assets/notifications/35-neo-message-dry.wav?url";
import signatureUrl from "@/assets/notifications/38-neo-signature.wav?url";

export type NotificationSound = "signature" | "message-dry";

export const NOTIFICATION_SOUND_URLS: Record<NotificationSound, string> = {
  signature: signatureUrl,
  "message-dry": messageDryUrl,
};

type AudioEnvironment = {
  createContext: () => AudioContext;
  fetchBytes: (url: string) => Promise<ArrayBuffer>;
};

function browserAudioEnvironment(): AudioEnvironment {
  return {
    createContext: () => {
      const AudioContextConstructor = window.AudioContext
        ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextConstructor) throw new Error("Web Audio is unavailable");
      return new AudioContextConstructor();
    },
    fetchBytes: async (url) => {
      const response = await fetch(url, { credentials: "same-origin" });
      if (!response.ok) throw new Error(`notification sound unavailable (${response.status})`);
      return response.arrayBuffer();
    },
  };
}

/**
 * One app-lifetime Web Audio graph. Preparation may fetch/decode asynchronously ahead of time;
 * `play` is deliberately synchronous and either starts a fresh source immediately or drops the
 * event. It never queues a backlog that could surprise the operator after autoplay unblocks.
 */
export class NotificationAudioEngine {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private buffers: Partial<Record<NotificationSound, AudioBuffer>> = {};
  private preparation: Promise<void> | null = null;
  private failed = false;

  constructor(
    private readonly environment: AudioEnvironment = browserAudioEnvironment(),
    private readonly urls: Record<NotificationSound, string> = NOTIFICATION_SOUND_URLS,
  ) {}

  prepare(): Promise<void> {
    if (this.preparation) return this.preparation;
    this.preparation = this.prepareOnce();
    return this.preparation;
  }

  private async prepareOnce(): Promise<void> {
    try {
      const context = this.environment.createContext();
      const master = context.createGain();
      const limiter = context.createDynamicsCompressor();
      // Leave substantial headroom before the limiter; simultaneous independent sources can
      // overlap without hard clipping or truncating each other's attack/tail.
      master.gain.value = 0.35;
      limiter.threshold.value = -12;
      limiter.knee.value = 0;
      limiter.ratio.value = 20;
      limiter.attack.value = 0.002;
      limiter.release.value = 0.1;
      master.connect(limiter);
      limiter.connect(context.destination);
      this.context = context;
      this.master = master;

      const [signatureBytes, messageDryBytes] = await Promise.all([
        this.environment.fetchBytes(this.urls.signature),
        this.environment.fetchBytes(this.urls["message-dry"]),
      ]);
      const [signature, messageDry] = await Promise.all([
        context.decodeAudioData(signatureBytes.slice(0)),
        context.decodeAudioData(messageDryBytes.slice(0)),
      ]);
      this.buffers = { signature, "message-dry": messageDry };
    } catch {
      this.failed = true;
      this.buffers = {};
    }
  }

  /** Resume on the first ordinary user gesture. A rejection affects audio only. */
  async unlock(): Promise<void> {
    void this.prepare();
    const context = this.context;
    if (!context || context.state === "running" || context.state === "closed") return;
    try {
      await context.resume();
    } catch {
      // Keep future notification events drop-only while blocked; never replay missed events.
    }
  }

  /** Start now, with no timer/fetch/decode/queue in the arrival path. */
  play(sound: NotificationSound): boolean {
    const context = this.context;
    const master = this.master;
    const buffer = this.buffers[sound];
    if (this.failed || !context || !master || !buffer || context.state !== "running") return false;
    try {
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(master);
      source.start();
      return true;
    } catch {
      return false;
    }
  }
}

/** Install both gesture paths and remove both as soon as either one fires. */
export function installNotificationAudioUnlock(
  engine: Pick<NotificationAudioEngine, "unlock">,
  target: Pick<Window, "addEventListener" | "removeEventListener"> = window,
): () => void {
  const unlock = () => {
    target.removeEventListener("pointerdown", unlock);
    target.removeEventListener("keydown", unlock);
    void engine.unlock();
  };
  target.addEventListener("pointerdown", unlock);
  target.addEventListener("keydown", unlock);
  return () => {
    target.removeEventListener("pointerdown", unlock);
    target.removeEventListener("keydown", unlock);
  };
}
